import { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Server,
  Database,
  Globe,
  Users,
  Building2,
  Monitor,
  Loader2,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Info,
  X
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

// Custom node component
const AssetNode = ({ data }) => {
  const getIcon = (type) => {
    const icons = {
      hardware: Server,
      software: Monitor,
      dados: Database,
      servico: Globe,
      pessoas: Users,
      instalacao: Building2
    };
    const Icon = icons[type] || Server;
    return <Icon className="h-4 w-4" />;
  };

  return (
    <div
      className={cn(
        "px-3 py-2 rounded-lg shadow-md border-2 min-w-[160px] max-w-[200px] bg-white",
        "hover:shadow-lg transition-shadow cursor-pointer"
      )}
      style={{ borderColor: data.borderColor }}
    >
      <div className="flex items-center gap-2 mb-1">
        <div
          className="p-1.5 rounded"
          style={{ backgroundColor: `${data.color}20`, color: data.color }}
        >
          {getIcon(data.assetType)}
        </div>
        <span className="text-xs font-medium text-slate-500 uppercase">
          {data.assetType}
        </span>
      </div>
      <p className="text-sm font-semibold text-slate-900 truncate" title={data.label}>
        {data.label}
      </p>
      {data.department && (
        <p className="text-xs text-slate-500 mt-1 truncate">{data.department}</p>
      )}
      <div className="flex items-center justify-between mt-2">
        <Badge
          variant="outline"
          className={cn(
            "text-[10px]",
            data.criticality === 'critica' ? 'bg-red-50 text-red-700 border-red-200' :
            data.criticality === 'alta' ? 'bg-orange-50 text-orange-700 border-orange-200' :
            data.criticality === 'media' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' :
            'bg-green-50 text-green-700 border-green-200'
          )}
        >
          {data.criticality}
        </Badge>
      </div>
    </div>
  );
};

const nodeTypes = {
  assetNode: AssetNode
};

export default function AssetDependencies() {
  const [isLoading, setIsLoading] = useState(true);
  const [graphData, setGraphData] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetDetails, setAssetDetails] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterCriticality, setFilterCriticality] = useState('');

  useEffect(() => {
    loadGraph();
  }, [filterType, filterCriticality]);

  const loadGraph = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('asset_type', filterType);
      if (filterCriticality) params.append('criticality', filterCriticality);
      
      const response = await api.get(`/asset-dependencies/graph?${params}`);
      setGraphData(response.data);
      setNodes(response.data.nodes || []);
      setEdges(response.data.edges || []);
    } catch (error) {
      toast.error('Erro ao carregar grafo de dependências');
    } finally {
      setIsLoading(false);
    }
  };

  const onNodeClick = useCallback(async (event, node) => {
    setSelectedAsset(node.data);
    try {
      const response = await api.get(`/asset-dependencies/asset/${node.id}/connections`);
      setAssetDetails(response.data);
    } catch (error) {
      console.error('Error loading asset details:', error);
    }
  }, []);

  const closeDetails = () => {
    setSelectedAsset(null);
    setAssetDetails(null);
  };

  const typeLabels = {
    hardware: 'Hardware',
    software: 'Software',
    dados: 'Dados',
    servico: 'Serviço',
    pessoas: 'Pessoas',
    instalacao: 'Instalação'
  };

  if (isLoading) {
    return (
      <Layout title="Mapa de Dependências">
        <div className="flex items-center justify-center h-[600px]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Mapa de Dependências de Ativos">
      <div className="space-y-4" data-testid="dependencies-container">
        {/* Stats & Filters */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{graphData?.stats?.total_assets || 0}</span> ativos
              <span className="mx-2">•</span>
              <span className="font-semibold text-slate-900">{graphData?.stats?.total_connections || 0}</span> ligações
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <select
              className="h-9 px-3 text-sm rounded-md border border-input bg-background"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">Todos os Tipos</option>
              {Object.entries(typeLabels).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            
            <select
              className="h-9 px-3 text-sm rounded-md border border-input bg-background"
              value={filterCriticality}
              onChange={(e) => setFilterCriticality(e.target.value)}
            >
              <option value="">Todas as Criticidades</option>
              <option value="critica">Crítica</option>
              <option value="alta">Alta</option>
              <option value="media">Média</option>
              <option value="baixa">Baixa</option>
            </select>
            
            <Button variant="outline" size="sm" onClick={loadGraph}>
              Atualizar
            </Button>
          </div>
        </div>

        {/* Graph Container */}
        <div className="relative h-[600px] bg-slate-50 rounded-xl border overflow-hidden">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            fitView
            minZoom={0.2}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'smoothstep',
              markerEnd: { type: MarkerType.ArrowClosed }
            }}
          >
            <Background color="#e2e8f0" gap={20} />
            <Controls />
            <MiniMap 
              nodeColor={(node) => node.data?.color || '#6b7280'}
              maskColor="rgba(255, 255, 255, 0.8)"
            />
            
            {/* Legend Panel */}
            <Panel position="top-left" className="bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm border">
              <h4 className="text-xs font-semibold text-slate-700 mb-2">Legenda</h4>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3b82f6' }}></div>
                  <span className="text-xs text-slate-600">Hardware</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#8b5cf6' }}></div>
                  <span className="text-xs text-slate-600">Software</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></div>
                  <span className="text-xs text-slate-600">Dados</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
                  <span className="text-xs text-slate-600">Serviço</span>
                </div>
              </div>
              <div className="border-t mt-2 pt-2">
                <p className="text-[10px] text-slate-500">Borda: Criticidade</p>
                <div className="flex items-center gap-1 mt-1">
                  <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#dc2626' }}></div>
                  <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#ea580c' }}></div>
                  <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#ca8a04' }}></div>
                  <div className="w-3 h-3 rounded border-2" style={{ borderColor: '#16a34a' }}></div>
                </div>
              </div>
            </Panel>
          </ReactFlow>

          {/* Asset Details Panel */}
          {selectedAsset && (
            <div className="absolute top-4 right-4 w-80 bg-white rounded-lg shadow-lg border p-4 z-10">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-semibold text-slate-900">{selectedAsset.label}</h3>
                  <p className="text-xs text-slate-500">{selectedAsset.assetId}</p>
                </div>
                <button onClick={closeDetails} className="text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" style={{ backgroundColor: `${selectedAsset.color}20`, color: selectedAsset.color }}>
                    {typeLabels[selectedAsset.assetType] || selectedAsset.assetType}
                  </Badge>
                  <Badge variant="outline" className={cn(
                    selectedAsset.criticality === 'critica' ? 'bg-red-50 text-red-700 border-red-200' :
                    selectedAsset.criticality === 'alta' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                    'bg-yellow-50 text-yellow-700 border-yellow-200'
                  )}>
                    {selectedAsset.criticality}
                  </Badge>
                </div>
                
                {assetDetails && (
                  <>
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1">Depende de ({assetDetails.dependencies?.length || 0})</p>
                      {assetDetails.dependencies?.length > 0 ? (
                        <div className="space-y-1">
                          {assetDetails.dependencies.slice(0, 5).map((dep) => (
                            <div key={dep.asset_id} className="text-xs p-1.5 bg-slate-50 rounded flex items-center justify-between">
                              <span className="truncate">{dep.name}</span>
                              <Badge variant="outline" className="text-[9px]">{dep.asset_type}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">Nenhuma dependência</p>
                      )}
                    </div>
                    
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-1">
                        Ativos dependentes ({assetDetails.dependents?.length || 0})
                        {assetDetails.impact_score > 0 && (
                          <span className="text-red-600 ml-1">⚠️ Impacto: {assetDetails.impact_score}</span>
                        )}
                      </p>
                      {assetDetails.dependents?.length > 0 ? (
                        <div className="space-y-1">
                          {assetDetails.dependents.slice(0, 5).map((dep) => (
                            <div key={dep.asset_id} className="text-xs p-1.5 bg-red-50 rounded flex items-center justify-between">
                              <span className="truncate">{dep.name}</span>
                              <Badge variant="outline" className="text-[9px]">{dep.asset_type}</Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400">Nenhum ativo depende deste</p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Info Card */}
        <Card className="card-flat bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900">Como usar o mapa</h4>
                <ul className="text-sm text-blue-700 mt-1 space-y-1">
                  <li>• Arraste para mover o mapa, scroll para zoom</li>
                  <li>• Clique num ativo para ver detalhes e dependências</li>
                  <li>• Setas animadas indicam ligações críticas</li>
                  <li>• Use os filtros para focar em tipos ou criticidades específicas</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
